import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { BlobStorageAccess } from './auth.module';
import { AuthService } from './auth.service';
import { assertBlobConfig } from './utils';

@Injectable()
export class BlobService {
  private readonly httpClient = inject(HttpClient);
  private readonly auth = inject(AuthService);

  upload(
    file: File,
    path: string = this.randomString(),
    sasParam?: string,
  ): Observable<string> {
    const sas = sasParam ?? this.blobParams.upload.sas;
    return new Observable((observer) => {
      const fileReader = new FileReader();
      fileReader.readAsArrayBuffer(file);
      fileReader.onload = (loaded) => {
        if (!loaded.target) throw new Error('Error reading file!');
        this.httpClient
          .put(this.getUrl(path, sas), loaded.target.result, {
            headers: new HttpHeaders({
              'x-ms-blob-type': 'BlockBlob',
              'x-ms-blob-content-type': file.type,
              'x-ms-meta-uploader': this.auth.userToken?.sub ?? '',
            }),
          })
          .subscribe(() => {
            observer.next(path);
            observer.complete();
          });
      };
    });
  }

  delete(path: string, sasParam?: string): Observable<void> {
    const sas = sasParam ?? this.blobParams.upload.sas;
    const blobUrl = this.getUrl(path, sas) + '&comp=metadata';
    return this.httpClient.head<void>(blobUrl, { observe: 'response' }).pipe(
      switchMap((metadataResponse) =>
        this.httpClient.put<void>(blobUrl, '', {
          headers: new HttpHeaders({
            'x-ms-meta-uploader': metadataResponse.headers.get(
              'x-ms-meta-uploader',
            ) as string,
            'x-ms-meta-deleted': new Date().toUTCString(),
          }),
        }),
      ),
    );
  }

  getUrl(path: string, sasParam?: string): string {
    const params = this.blobParams.upload;
    return `${params.url}/${path}?${sasParam ?? params.sas}`;
  }

  private get blobParams(): BlobStorageAccess {
    assertBlobConfig(this.auth.blob);
    return this.auth.blob;
  }

  private randomString(length = 64): string {
    const s = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    return Array(length)
      .join()
      .split(',')
      .map(() => s.charAt(Math.floor(Math.random() * s.length)))
      .join('');
  }
}
